import { useQuery } from '@tanstack/react-query';
import { SessionPpgData } from '@repo/shared/dtos/devices/SessionPpgDto';
import { myInsightsQueryKeys } from './queryKeys';
import { ANALYTICS_SESSIONS_BASE_URL, CACHE_DURATION_MS, DEFAULT_SAMPLE_SIZE } from '../config/constants';

const MAX_ERROR_BODY_LENGTH = 200;

async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const text = await response.text();
    if (!text) return 'Failed to load PPG data';
    try {
      const payload = JSON.parse(text) as { message?: string };
      if (payload.message) return payload.message.slice(0, MAX_ERROR_BODY_LENGTH);
    } catch {
      // not JSON
    }
    return text.slice(0, MAX_ERROR_BODY_LENGTH);
  } catch {
    return 'Failed to load PPG data';
  }
}

async function fetchSessionPpg(sessionId: string, limit: number): Promise<SessionPpgData | null> {
  const params = new URLSearchParams({ limit: String(limit) });

  const response = await fetch(`${ANALYTICS_SESSIONS_BASE_URL}/${sessionId}/ppg?${params}`);

  if (!response.ok) {
    if (response.status === 404) return null;
    const message = await extractErrorMessage(response);
    throw new Error(message);
  }

  const text = await response.text();
  if (!text) return null;
  return JSON.parse(text) as SessionPpgData;
}

export const useSessionPpg = (sessionId?: string, options?: { limit?: number }) => {
  const limit = options?.limit ?? DEFAULT_SAMPLE_SIZE;
  const { data: ppgData = null, isLoading, error } = useQuery({
    queryKey: myInsightsQueryKeys.sessionDetail.ppg(sessionId ?? '', limit),
    queryFn: () => fetchSessionPpg(sessionId!, limit),
    enabled: !!sessionId,
    retry: 1,
    staleTime: CACHE_DURATION_MS,
  });

  return { ppgData, isLoading, error: error as Error | null };
};
